import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { faqService } from "@/features/faqs/services/faqService";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { queryKeys } from "@/lib/query-keys";
import type {
  FaqCategoryInput,
  FaqQuestionInput,
  ListFaqQuestionsQuery,
} from "@/types/faq";

/**
 * FAQ data for the dashboard screen.
 *
 * 🔴 EVERY QUERY IS GATED ON `faq:manage`, the permission the backend handler checks. Not on
 * `system:view-dashboard`, which opens routes and unlocks no data — gating a fetch on that is
 * what made the dashboard's own screen answer 403s to anyone holding it alone.
 */
export function useFaqPermissions() {
  const { canManageFaqs } = useUserPermissions();
  return { canManageFaqs };
}

export function useFaqCategories() {
  const { canManageFaqs } = useFaqPermissions();
  return useQuery({
    queryKey: queryKeys.faqs.categories(),
    queryFn: () => faqService.listCategories(),
    enabled: canManageFaqs,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFaqQuestions(query: ListFaqQuestionsQuery) {
  const { canManageFaqs } = useFaqPermissions();
  return useQuery({
    queryKey: queryKeys.faqs.questionList(query),
    queryFn: () => faqService.listQuestions(query),
    enabled: canManageFaqs,
  });
}

/**
 * Both lists are invalidated after every write, including question writes.
 *
 * ⚠️ That is not over-invalidation: moving a question between categories changes what the
 * category list's counts should say, and deleting the last question in a category is what makes
 * that category deletable. Refreshing only the list that was written leaves the other one quietly
 * describing a state that no longer exists.
 */
function useFaqInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.faqs.all });
  };
}

export function useCreateFaqQuestion() {
  const invalidate = useFaqInvalidation();
  return useMutation({
    mutationFn: (input: FaqQuestionInput) => faqService.createQuestion(input),
    onSuccess: () => {
      invalidate();
      toast.success("تمت إضافة السؤال");
    },
    onError: (e: Error) => toast.error(e.message || "تعذّر إضافة السؤال"),
  });
}

export function useUpdateFaqQuestion() {
  const invalidate = useFaqInvalidation();
  return useMutation({
    mutationFn: ({
      code,
      input,
    }: {
      code: string;
      input: Partial<Omit<FaqQuestionInput, "code">>;
    }) => faqService.updateQuestion(code, input),
    onSuccess: () => {
      invalidate();
      toast.success("تم حفظ السؤال");
    },
    onError: (e: Error) => toast.error(e.message || "تعذّر حفظ السؤال"),
  });
}

export function useDeleteFaqQuestion() {
  const invalidate = useFaqInvalidation();
  return useMutation({
    mutationFn: (code: string) => faqService.deleteQuestion(code),
    onSuccess: () => {
      invalidate();
      toast.success("تم حذف السؤال");
    },
    onError: (e: Error) => toast.error(e.message || "تعذّر حذف السؤال"),
  });
}

export function useCreateFaqCategory() {
  const invalidate = useFaqInvalidation();
  return useMutation({
    mutationFn: (input: FaqCategoryInput) => faqService.createCategory(input),
    onSuccess: () => {
      invalidate();
      toast.success("تمت إضافة القسم");
    },
    onError: (e: Error) => toast.error(e.message || "تعذّر إضافة القسم"),
  });
}

export function useUpdateFaqCategory() {
  const invalidate = useFaqInvalidation();
  return useMutation({
    mutationFn: ({
      key,
      input,
    }: {
      key: string;
      input: Partial<Omit<FaqCategoryInput, "key">>;
    }) => faqService.updateCategory(key, input),
    onSuccess: () => {
      invalidate();
      toast.success("تم حفظ القسم");
    },
    onError: (e: Error) => toast.error(e.message || "تعذّر حفظ القسم"),
  });
}

export function useDeleteFaqCategory() {
  const invalidate = useFaqInvalidation();
  return useMutation({
    mutationFn: (key: string) => faqService.deleteCategory(key),
    onSuccess: () => {
      invalidate();
      toast.success("تم حذف القسم");
    },
    // The backend refuses to delete a category that still holds questions and says how many.
    // Surfacing its message verbatim is the whole point — a generic failure toast would leave the
    // user with no idea what to do next.
    onError: (e: Error) => toast.error(e.message || "تعذّر حذف القسم"),
  });
}
